import React from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "../../../../components/form/Button";
import { styles } from "../../index";
import { entityLabel } from "./entityLabels";
import { fieldLabel } from "./fieldLabels";
import { formatAuditValue } from "./valueFormatter";

const thStyle = { textAlign: "left", borderBottom: "1px solid #ccc", padding: 6 };
const tdStyle = { padding: 6, borderBottom: "1px solid #eee" };

// Group the flat FieldChangeDTO[] into one block per touched (sub-)entity.
const groupChanges = (detail) => {
  const groups = [];
  const byKey = {};
  (detail || []).forEach((d) => {
    const key = d.entityId || d.entityType || "_";
    if (!byKey[key]) {
      byKey[key] = { typeLabel: entityLabel(d.entityType), label: d.group, rows: [] };
      groups.push(byKey[key]);
    }
    byKey[key].rows.push(d);
  });
  return groups;
};

const blockHeading = (g) => {
  const type = g.typeLabel || "Změny";
  return g.label ? `${type} — ${g.label}` : type;
};

const AuditHistoryDetailPanel = ({
  selected,
  detail,
  loading,
  error,
  ownerHref,
  ownerHistoryHref,
  onClose,
  onOwnerNavigate,
  onOpenStatementDiff,
}) => {
  if (!selected) return null;
  const ownerTypeLabel = entityLabel(selected.ownerType);
  const isStatement = selected.detailType === "STATEMENT";
  const isNote = selected.action === "NOTE";
  const isAppeal = selected.action === "APPEAL_SENT";
  const isTextOnly = isNote || isAppeal;
  const textHeading = isAppeal ? "Výzva" : "Poznámka";
  const ownerDeleted = selected.ownerActive === false;
  const ownerTargetHref = ownerDeleted ? ownerHistoryHref : ownerHref;
  const ownerButtonDisabled = ownerDeleted && !ownerHistoryHref;
  const ownerButtonLabel =
    (ownerDeleted
      ? (ownerHistoryHref ? "Zobrazit historii smazaného " : "Smazáno — ")
      : "Přejít na ") +
    (ownerTypeLabel || "").toLowerCase() +
    (selected.ownerLabel ? " — " + selected.ownerLabel : "");
  const ownerButtonTitle = ownerButtonDisabled
    ? "Entita byla smazána — pro tento typ neexistuje stránka historie."
    : undefined;
  const showOwnerButton = ownerHref || ownerHistoryHref || ownerDeleted;
  const titleText = selected.entityType
    ? `${entityLabel(selected.entityType)}${selected.entityLabel ? " — " + selected.entityLabel : ""}`
    : "Detail";
  return (
    <Dialog open={!!selected} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{titleText}</DialogTitle>
      <DialogContent>
        {showOwnerButton && (
          <div style={{ marginBottom: 12 }}>
            <Button
              type="button"
              style={styles.buttons}
              disabled={ownerButtonDisabled}
              title={ownerButtonTitle}
              label={ownerButtonLabel}
              onClick={() => {
                if (ownerButtonDisabled || !ownerTargetHref) return;
                if (typeof onOwnerNavigate === "function") onOwnerNavigate(ownerTargetHref);
              }}
            />
          </div>
        )}
        {loading && <div>Načítám…</div>}
        {error && <div style={{ color: "red" }}>{error}</div>}
        {!loading && !error && isTextOnly && (
          <div style={{ whiteSpace: "pre-wrap" }}>
            <h4 style={{ margin: "8px 0" }}>{textHeading}</h4>
            {(isAppeal ? detail && detail[0] && detail[0].newValue : selected.detailRef) || (
              <em>Bez textu.</em>
            )}
          </div>
        )}
        {!loading && !error && !isTextOnly && !isStatement && detail && detail.length === 0 && (
          <div><em>Žádné změny k zobrazení.</em></div>
        )}
        {!loading && !error && !isTextOnly && !isStatement && detail && detail.length > 0 &&
          groupChanges(detail).map((g, gi) => (
            <div key={gi} style={{ marginBottom: 16 }}>
              <h4 style={{ margin: "8px 0" }}>{blockHeading(g)}</h4>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th style={thStyle}>Pole</th>
                    <th style={thStyle}>Původní hodnota</th>
                    <th style={thStyle}>Nová hodnota</th>
                  </tr>
                </thead>
                <tbody>
                  {g.rows.map((d, i) => (
                    <tr key={i}>
                      <td style={tdStyle}>{fieldLabel(d.entityType, d.field) || d.fieldLabel || d.field}</td>
                      <td style={tdStyle}>{formatAuditValue(d.oldValue, d.fieldType, d.entityType, d.field)}</td>
                      <td style={tdStyle}>{formatAuditValue(d.newValue, d.fieldType, d.entityType, d.field)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
      </DialogContent>
      <DialogActions>
        {isStatement && !loading && (
          <Button
            type="button"
            style={{ ...styles.buttons, marginRight: 8 }}
            label="Zobrazit změny oznámení"
            onClick={() => onOpenStatementDiff(selected)}
          />
        )}
        <Button
          type="button"
          style={styles.buttons}
          label="Zavřít"
          onClick={onClose}
        />
      </DialogActions>
    </Dialog>
  );
};

export default AuditHistoryDetailPanel;
